'use client';

import { useComposedRefs } from '@radix-ui/react-compose-refs';
import { type VariantProps } from 'class-variance-authority';

import { buttonVariants } from '@/components/atoms/Button';
import { Link as NextLink } from '@/i18n/routing';
import { useAccessibleNameWarning } from '@/lib/hooks/util/useAccessibleNameWarning';
import { cn } from '@/lib/utils/tailwind';

import type { ComponentProps, MouseEvent, ReactNode } from 'react';

const linkButtonVariants = buttonVariants;

export interface LinkButtonProps
  extends
    Omit<ComponentProps<typeof NextLink>, 'href' | 'children' | 'onClick'>,
    VariantProps<typeof linkButtonVariants> {
  href: string;
  children: ReactNode;
  className?: string;
  disabled?: boolean;
  onClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
}

export const LinkButton = ({
  href,
  children,
  className,
  variant = 'primary',
  size = 'md',
  disabled = false,
  onClick,
  ref,
  ...props
}: LinkButtonProps) => {
  const accessibleNameRef = useAccessibleNameWarning('LinkButton');
  const composedRef = useComposedRefs<HTMLAnchorElement>(
    accessibleNameRef,
    ref,
  );
  const classes = cn(linkButtonVariants({ variant, size }), className);

  if (disabled) {
    return (
      <span
        ref={accessibleNameRef}
        role="link"
        aria-disabled="true"
        tabIndex={0}
        className={classes}
        id={props.id}
        title={props.title}
        aria-label={props['aria-label']}
      >
        {children}
      </span>
    );
  }

  return (
    <NextLink
      ref={composedRef}
      href={href}
      className={classes}
      {...props}
      onClick={onClick}
    >
      {children}
    </NextLink>
  );
};
